import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormButton, FormDatePicker, FormSelect, FormTextInput, ToastSeverity, useToast } from 'thermia-react-container';
import * as yup from 'yup';
import { countryArray } from '../../../../../translations/locales/countryArray';
import '../../../../../translations/yup/yup';
import { Registration } from '../../../../../types/registrationTypes';
import { customerIsComplete, locationIsComplete } from '../../../../../validation/registration';
import RegistrationLocationYup from '../../../../../validation/yup-input/registrationLocation';
import CompleteRegistrationButton from '../../../components/completeRegistrationButton';
import { StyledContainer } from '../../../registration';
import {RegistrationsContext} from "../../../../../context/contextProvider";
import {MarketSpecificFieldType} from "../../../../../types/onsiteConfig";
import {useIsAdmin} from "../../../../../hooks/useIsAdmin";

type Props = {
  registration?: Registration | null;
  defaultCountry: string;
  updateLocation: (data: Registration['location']) => Promise<void>;
  loading: boolean;
  preventSwitchTabDueToUnsavedData: (value: boolean) => void;
  salesMarketForActivePartner: string;
  disabled: boolean;
};

interface EvaluatedAddress {
  useCustomerAddress: boolean;
  installationAddress: string;
  zipCode: string;
  city: string;
  country: string;
}

type CountryForSelect = {
  value: string;
  label: string;
};

const LocationForm: FC<Props> = ({
  registration,
  defaultCountry,
  updateLocation,
  loading,
  preventSwitchTabDueToUnsavedData,
  salesMarketForActivePartner,
  disabled
}) => {
  const { config } = useContext(RegistrationsContext);
  const showPropertyType = !!(
    config.installationConfig?.marketSpecificFields.find(
      x => x.field === MarketSpecificFieldType.PropertyType
    )?.validForSalesMarkets.includes(registration?.market ?? salesMarketForActivePartner)
  );
  
  const initIsUsingCustomerAddress = (
    activeRegistration: Registration | undefined | null,
    salesMarketForActivePartner: string,
  ): EvaluatedAddress => {
    if (
      customerIsComplete(activeRegistration?.customer) &&
      !locationIsComplete(activeRegistration?.location) &&
      activeRegistration?.customer?.country === salesMarketForActivePartner
    ) {
      return {
        useCustomerAddress: true,
        installationAddress: activeRegistration?.customer?.address ?? '',
        zipCode: activeRegistration?.customer?.zipCode ?? '',
        city: activeRegistration?.customer?.city ?? '',
        country: activeRegistration?.customer?.country ?? ''
      };
    }

    const locationAddressIsFilledIn =
      activeRegistration?.location?.installationAddress !== '' &&
      activeRegistration?.location?.installationAddress !== undefined &&
      activeRegistration?.location?.city !== '' &&
      activeRegistration?.location?.city !== undefined &&
      activeRegistration?.location?.zipCode !== '' &&
      activeRegistration?.location?.zipCode !== undefined;

    const isCustomerAndLocationAddressSame =
      activeRegistration?.customer?.address === activeRegistration?.location?.installationAddress &&
      activeRegistration?.customer?.zipCode === activeRegistration?.location?.zipCode &&
      activeRegistration?.customer?.city === activeRegistration?.location?.city &&
      activeRegistration?.customer?.country === activeRegistration?.location?.country;

    return {
      useCustomerAddress: locationAddressIsFilledIn && isCustomerAndLocationAddressSame,
      installationAddress: activeRegistration?.location?.installationAddress ?? '',
      zipCode: activeRegistration?.location?.zipCode ?? '',
      city: activeRegistration?.location?.city ?? '',
      country: activeRegistration?.location?.country ?? salesMarketForActivePartner
    };
  };
  const initialAddress: EvaluatedAddress = initIsUsingCustomerAddress(registration, salesMarketForActivePartner);
  const todaydateYYMMDD = new Date().toISOString().split('T')[0];
  const { t } = useTranslation('registrations');
  const isAdmin = useIsAdmin();
  // Get the earliest delivery date of devices, used to validate that installation date of location
  // is not before any of the devices delivery dates. Administrators are allowed to
  // set any installation date, so for them minDate is undefined.
  const minDate = isAdmin ? undefined : registration?.devices?.items
    .map(device => device.deliverDate)
    .filter((date): date is string => date !== undefined)
    .reduce<string | undefined>((min, date) => (min === undefined || date < min ? date : min), undefined);
  type ValidatedSchema = yup.Asserts<typeof schema>;
  const schema: yup.SchemaOf<Registration['location']> = RegistrationLocationYup(showPropertyType, minDate);

  const { addToast } = useToast();

  const [useCustomerAddress, setUseCustomerAddress] = useState<boolean>(initialAddress.useCustomerAddress);
  const [previousState, setPreviousState] = useState<Registration['location']>({});

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty }
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues: {
      propertyType: registration?.location?.propertyType ?? '',
      installationAddress: initialAddress.installationAddress ?? '',
      zipCode: initialAddress.zipCode ?? '',
      city: initialAddress.city ?? '',
      country: initialAddress.country ?? defaultCountry,
      installationDate: registration?.location?.installationDate ?? todaydateYYMMDD
    }
  });

  useEffect(() => {
    if (preventSwitchTabDueToUnsavedData) {
      preventSwitchTabDueToUnsavedData(isDirty);
    }
  }, [preventSwitchTabDueToUnsavedData, isDirty]);

  const city = watch('city');
  const country = watch('country');
  const installationAddress = watch('installationAddress');
  const zipCode = watch('zipCode');
  const propertyType = watch('propertyType');
  const installationDate = watch('installationDate');

  const handleUseCustomerAddress = () => {
    if (!useCustomerAddress) {
      if (!customerIsComplete(registration?.customer)) {
        return addToast(t('registrations.general.errorUnableToUseCustomerAddressNotCompleted'), ToastSeverity.ERROR);
      }

      if (salesMarketForActivePartner === '') {
        return addToast(t('registrations.general.errorUnableToUseCustomerAddressInvalidCountry'), ToastSeverity.ERROR);
      }

      if (registration?.customer?.country !== salesMarketForActivePartner) {
        return addToast(t('registrations.general.errorUnableToUseCustomerAddressInvalidCountry'), ToastSeverity.ERROR);
      }

      setPreviousState({
        city,
        installationAddress,
        zipCode,
        propertyType,
        installationDate,
        country
      });
      reset(
        {
          city: registration?.customer?.city ?? '',
          country: registration?.customer?.country ?? salesMarketForActivePartner,
          installationAddress: registration?.customer?.address ?? '',
          zipCode: registration?.customer?.zipCode ?? '',
          propertyType,
          installationDate
        },
        {
          keepDefaultValues: true
        }
      );
      return setUseCustomerAddress(true);
    }

    reset(
      { ...previousState, propertyType, installationDate },
      {
        keepDefaultValues: true
      }
    );
    setUseCustomerAddress(false);
  };

  const onValid = async (data: ValidatedSchema) => {
    const registrationLocation: Registration['location'] = {
      propertyType: data?.propertyType,
      installationAddress: data?.installationAddress ?? '',
      zipCode: data?.zipCode ?? '',
      city: data?.city ?? '',
      installationDate: data?.installationDate ?? '',
      country: data?.country ?? ''
    };
    try {
      await updateLocation(registrationLocation);
      reset(registrationLocation);
    } catch (error) {}
  };

  const partnersAreas: CountryForSelect[] = useMemo(
    () =>
      countryArray.reduce((prev: CountryForSelect[], next) => {
        if (salesMarketForActivePartner === next.countryCode) {
          prev.push({
            label: `${next.flag} ${t(next.countryCode)}`,
            value: next.countryCode
          });
        }
        return prev;
      }, []),
    [t, salesMarketForActivePartner]
  );

  return (
    <>
      <StyledContainer>
        <Grid container>
          <Grid item xs={8} container spacing={2} rowSpacing={4}>
            { showPropertyType && ( // property identifier is only used on the swedish market
              <Grid item xs={12}>
                <FormTextInput
                  control={control}
                  name={'propertyType'}
                  label={t('registrations.general.propertyType')}
                  disabled={disabled}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              {!disabled ? (
                <FormDatePicker name={'installationDate'} label={t('registrations.general.installationDate')} control={control} />
              ) : (
                <Grid item xs={8}>
                  <FormTextInput control={control} name={'installationDate'} label={t('registrations.general.installationDate')} disabled />
                </Grid>
              )}
            </Grid>
            {registration?.customer?.country === salesMarketForActivePartner && !disabled ? (
              <Grid item xs={12}>
                <Checkbox label={t('registrations.general.useCustomerAddress')} checked={useCustomerAddress} onClick={handleUseCustomerAddress} />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormTextInput
                control={control}
                name={'installationAddress'}
                label={t('registrations.general.installationAddress')}
                disabled={useCustomerAddress || disabled}
              />
            </Grid>
            <Grid item xs={4}>
              <FormTextInput
                control={control}
                name={'zipCode'}
                label={t('registrations.general.zipCode')}
                disabled={useCustomerAddress || disabled}
              />
            </Grid>
            <Grid item xs={8}>
              <FormTextInput control={control} name={'city'} label={t('registrations.general.city')} disabled={useCustomerAddress || disabled} />
            </Grid>
            <Grid item xs={5}>
              <FormSelect
                name={'country'}
                variant={'outlined'}
                label={t('registrations.general.selectCountry')}
                control={control}
                labelId="selectCountryLocation-select-label"
                options={partnersAreas}
                disabled={useCustomerAddress || disabled}
              />
            </Grid>
          </Grid>
        </Grid>
      </StyledContainer>
      {!disabled && (
        <Grid container spacing={2} justifyContent={'end'} alignItems={'center'} marginTop={2}>
          <Grid item>
            <FormButton
              variant={'contained'}
              onClick={handleSubmit(onValid)}
              title={t('registrations.general.save')}
              loading={loading}
            />
          </Grid>
          <Grid item>
            <CompleteRegistrationButton formIsDirty={isDirty} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default LocationForm;
